import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { serviceAPI } from '../api/serviceAPI';
import { useAuth } from '../contexts/AuthContext';
import { categoryLabel } from '../i18n/categoryNames';
import { formatRating } from '../utils/formatUtils';
import './MyServicesPage.css';

interface Service {
  id: number;
  title: string;
  description: string;
  price_basic: number;
  delivery_days_basic: number;
  category_name: string;
  status: string;
  views_count: number;
  orders_count: number;
  rating: number;
  reviews_count: number;
  created_at: string;
  updated_at: string;
}

const MyServicesPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useAuth();
  const [services, setServices] = useState<Service[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);

  console.log('MyServicesPage: 렌더링', { user });

  const loadServices = useCallback(async () => {
    if (!user) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const response = await serviceAPI.getServicesByUserId(user.id, user.nickname);
      setServices(response.services || []);
    } catch (err: any) {
      console.error('MyServicesPage: 서비스 목록 로딩 실패', err);
      setError(err.response?.data?.message || t('myServices.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [user, t]);

  useEffect(() => {
    loadServices();
  }, [loadServices]);

  const handleEdit = (serviceId: number) => {
    console.log('MyServicesPage: 서비스 수정 클릭', serviceId);
    navigate(`/service-edit/${serviceId}`);
  };

  const handleDeleteClick = (serviceId: number) => {
    console.log('MyServicesPage: 삭제 확인 팝업', serviceId);
    setDeleteConfirm(serviceId);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirm) return;

    try {
      console.log('MyServicesPage: 서비스 삭제 시작', deleteConfirm);
      await serviceAPI.deleteService(deleteConfirm);
      console.log('MyServicesPage: 서비스 삭제 성공');
      
      // 목록 새로고침
      setServices(services.filter(s => s.id !== deleteConfirm));
      setDeleteConfirm(null);
      alert(t('myServices.deleted'));
    } catch (err: any) {
      console.error('MyServicesPage: 서비스 삭제 실패', err);
      alert(err.response?.data?.message || t('myServices.deleteFailed'));
    }
  };

  const handleDeleteCancel = () => {
    console.log('MyServicesPage: 삭제 취소');
    setDeleteConfirm(null);
  };

  const getStatusText = (status: string) => {
    const statusMap: { [key: string]: string } = {
      'active': t('myServices.statusActive'),
      'paused': t('myServices.statusPaused'),
      'inactive': t('myServices.statusInactive'),
    };
    return statusMap[status] || status;
  };

  const getStatusClass = (status: string) => {
    return `status-badge status-${status}`;
  };

  if (loading) {
    return (
      <div className="my-services-page">
        <div className="container">
          <div className="loading">{t('myServices.loading')}</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="my-services-page">
        <div className="container">
          <div className="error-message">{error}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="my-services-page">
      <div className="container">
        <div className="page-header">
          <h1>{t('myServices.title')}</h1>
          <button 
            className="btn-create"
            onClick={() => navigate('/service-create')}
          >
            + {t('myServices.newService')}
          </button>
        </div>

        {services.length === 0 ? (
          <div className="empty-state">
            <p>{t('myServices.empty')}</p>
            <button 
              className="btn-create"
              onClick={() => navigate('/service-create')}
            >
              {t('myServices.firstService')}
            </button>
          </div>
        ) : (
          <div className="services-grid">
            {services.map((service) => (
              <div 
                key={service.id} 
                className="service-card"
                onClick={() => navigate(`/services/${service.id}`)}
                style={{ cursor: 'pointer' }}
              >
                <div className="card-header">
                  <span className={getStatusClass(service.status)}>
                    {getStatusText(service.status)}
                  </span>
                  <span className="category">{categoryLabel(service.category_name)}</span>
                </div>
                
                <h3 className="service-title">{service.title}</h3>
                
                <p className="service-description">
                  {service.description?.substring(0, 100)}
                  {service.description?.length > 100 && '...'}
                </p>
                
                <div className="service-stats">
                  <div className="stat-item">
                    <span className="stat-icon">⭐</span>
                    <span className="stat-value">
                      {formatRating(service.rating)}
                    </span>
                    <span className="stat-label">
                      ({service.reviews_count || 0})
                    </span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-icon">👁️</span>
                    <span className="stat-value">{service.views_count || 0}</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-icon">📦</span>
                    <span className="stat-value">{service.orders_count || 0}</span>
                  </div>
                </div>
                
                <div className="service-pricing">
                  <div className="price">
                    <span className="price-amount">
                      {t('projects.budgetExact', { v: service.price_basic?.toLocaleString() })}
                    </span>
                    <span className="delivery-time">
                      {t('myServices.daysTaken', { n: service.delivery_days_basic })}
                    </span>
                  </div>
                </div>
                
                <div className="card-footer">
                  <span className="created-date">
                    {t('common.createdDate')} {new Date(service.created_at).toLocaleDateString()}
                  </span>
                  <div className="action-buttons">
                    <button 
                      className="btn-view"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/services/${service.id}`);
                      }}
                    >
                      {t('common.view')}
                    </button>
                    <button 
                      className="btn-edit"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEdit(service.id);
                      }}
                    >
                      {t('common.edit')}
                    </button>
                    <button 
                      className="btn-delete"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteClick(service.id);
                      }}
                    >
                      {t('common.delete')}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* 삭제 확인 모달 */}
      {deleteConfirm && (
        <div className="modal-overlay" onClick={handleDeleteCancel}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>{t('myServices.deleteTitle')}</h3>
            <p>{t('myServices.deleteConfirmMsg')}</p>
            <p className="warning">{t('myServices.deleteWarning')}</p>
            <div className="modal-buttons">
              <button 
                className="btn-cancel"
                onClick={handleDeleteCancel}
              >
                {t('common.cancel')}
              </button>
              <button 
                className="btn-confirm-delete"
                onClick={handleDeleteConfirm}
              >
                {t('common.delete')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyServicesPage;
